import { get } from "lodash";
import React, { useCallback } from "react";
import shallow from "zustand/shallow";
import { useGoogleAnalytics } from "../context/googleAnalytics";
import useCurrency, { CurrencyKey } from "../utils/useCurrency";
import { useHntMarket } from "../utils/useHntMarket";

interface Props {
  className?: string;
}

const CurrencySelector: React.FC<Props> = ({ className }) => {
  const { hntMarket } = useHntMarket();
  const { trackEvent } = useGoogleAnalytics();
  const [currencyOptions, getCurrencyTitle, setCurrency, currency] =
    useCurrency(
      (state) => [
        state.currencyOptions,
        state.getCurrencyTitle,
        state.setCurrency,
        state.currency
      ],
      shallow
    );

  const renderCurrencyOption = useCallback(
    (key: CurrencyKey) => {
      const price = get(hntMarket, [
        "prices",
        key === CurrencyKey.hnt ? CurrencyKey.usd : key
      ]);

      if (!price) return;

      switch (key) {
        case CurrencyKey.btc:
        case CurrencyKey.eth:
          return `${(price * 1000).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}m${getCurrencyTitle(key)}/HNT`;
        default:
          return `${price.toLocaleString("en-US", {
            style: "currency",
            currency: key === CurrencyKey.hnt ? CurrencyKey.usd : key,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}/HNT`;
      }
    },
    [getCurrencyTitle, hntMarket]
  );

  return (
    <div className={className}>
      <select
        id="currency"
        className="w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
        onChange={(e) => {
          trackEvent("update currency", {
            event_category: "user settings",
            event_label: e.target.value
          });
          setCurrency(e.target.value as CurrencyKey);
        }}
        value={currency}
      >
        {currencyOptions.map(({ key }) => (
          <option key={key} value={key}>
            {getCurrencyTitle(key as CurrencyKey).padEnd(7, "_")}
            {renderCurrencyOption(key as CurrencyKey)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CurrencySelector;

//
// Utils
//

export const USD_CURRENCY_LOCALE_STRING_CONFIG = {
  style: "currency",
  currency: CurrencyKey.usd,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
};
